// Mobile bottom sheets: overlay/sheet creation, open/close/toggle, drag gesture, and per-sheet content rendering.

import { store } from '../../../core/store.js'
import { getCdnThumbnail } from '../../../core/config.js'
import { state } from './state.js'
import { ICONS } from './icons.js'

/**
 * Create shared sheet overlay and sheet containers
 */
export function createSheetOverlay() {
  // Overlay
  const overlay = document.createElement('div')
  overlay.className = 'ws-mobile-sheet-overlay'
  overlay.id = 'wsMobileSheetOverlay'
  overlay.onclick = () => closeSheet()
  document.body.appendChild(overlay)

  // Sheets
  createSheet('preview', 'Preview', renderPreviewContent)
  createSheet('arts', 'Arts', renderArtsContent)
  createSheet('products', 'Products', renderProductsContent)
}

function createSheet(id, title, _renderFn) {
  const sheet = document.createElement('div')
  sheet.className = 'ws-mobile-sheet'
  sheet.id = `wsMobileSheet-${id}`
  sheet.dataset.sheet = id

  sheet.innerHTML = `
    <div class="ws-mobile-sheet-handle"></div>
    <div class="ws-mobile-sheet-header">
      <span class="ws-mobile-sheet-title">${title}</span>
      <button class="ws-mobile-sheet-close">${ICONS.x}</button>
    </div>
    <div class="ws-mobile-sheet-body" id="wsMobileSheetBody-${id}">
      <div class="ws-mobile-sheet-grid">
        ${Array(6).fill('<div class="ws-mobile-skeleton ws-mobile-skeleton-card"></div>').join('')}
      </div>
    </div>
  `

  document.body.appendChild(sheet)

  sheet.querySelector('.ws-mobile-sheet-close').onclick = () => closeSheet()
  setupSheetDrag(sheet.querySelector('.ws-mobile-sheet-handle'), sheet)
}

/**
 * Toggle sheet
 */
export function toggleSheet(id) {
  if (state.activeSheet === id) {
    closeSheet()
  } else {
    openSheet(id)
  }
}

/**
 * Open the sheet with the given id (closing any other open sheet first)
 */
export function openSheet(id) {
  if (state.activeSheet && state.activeSheet !== id) {
    closeSheet(false)
  }

  state.activeSheet = id

  // Update FAB active states
  document.querySelectorAll('.ws-mobile-fab-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.sheet === id)
  })

  // Show overlay
  const overlay = document.getElementById('wsMobileSheetOverlay')
  if (overlay) overlay.classList.add('active')

  // Open sheet
  const sheet = document.getElementById(`wsMobileSheet-${id}`)
  if (sheet) {
    sheet.classList.add('open')
    sheet.classList.remove('peek', 'half')
  }

  // Render content
  renderSheetContent(id)
}

/**
 * Close the currently open sheet (optionally without animating the overlay away)
 */
export function closeSheet(animateOverlay = true) {
  if (!state.activeSheet) return

  const sheet = document.getElementById(`wsMobileSheet-${state.activeSheet}`)
  if (sheet) sheet.classList.remove('open', 'peek', 'half')

  document.querySelectorAll('.ws-mobile-fab-btn').forEach(btn => {
    btn.classList.remove('active')
  })

  if (animateOverlay) {
    const overlay = document.getElementById('wsMobileSheetOverlay')
    if (overlay) overlay.classList.remove('active')
  }

  state.activeSheet = null
}

/**
 * Render sheet content
 */
function renderSheetContent(id) {
  switch (id) {
    case 'preview': renderPreviewContent(); break
    case 'arts': renderArtsContent(); break
    case 'products': renderProductsContent(); break
  }
}

/**
 * Render the preview sheet body (mirrors #previewBoxContent + product info)
 */
export function renderPreviewContent() {
  const body = document.getElementById('wsMobileSheetBody-preview')
  if (!body) return

  const product = store.getState('design.currentProduct')
  const previewBox = document.getElementById('previewBoxContent')

  body.innerHTML = `
    <div class="ws-mobile-sheet-preview">
      <div class="preview-image">
        ${previewBox ? previewBox.innerHTML : '<div class="ws-mobile-skeleton" style="width:100%;aspect-ratio:1;"></div>'}
      </div>
      <div class="preview-info">
        <div class="preview-product-name">${product?.sds_par_english_name /* legacy-ref-ok: v3.2 rename pending */ || product?.name || 'Product'}</div>
        <div class="preview-product-price">${(p => p ? '$' + p : '')(product?.current_price || product?.price)}</div>
      </div>
    </div>
  `
}

function renderArtsContent() {
  const body = document.getElementById('wsMobileSheetBody-arts')
  if (!body) return

  const materials = window.MaterialManager?.state?.materials || []

  if (materials.length === 0) {
    body.innerHTML = `
      <div class="ws-mobile-sheet-grid">
        ${Array(6).fill('<div class="ws-mobile-skeleton ws-mobile-skeleton-card"></div>').join('')}
      </div>
    `
    return
  }

  const grid = document.createElement('div')
  grid.className = 'ws-mobile-sheet-grid'

  materials.forEach((material, idx) => {
    const item = document.createElement('div')
    item.className = 'ws-mobile-material-item'
    item.onclick = () => {
      window.MaterialManager?.addMaterialToDesign?.(material.url, material.id, material.title)
      closeSheet()
    }

    const img = document.createElement('img')
    img.src = getCdnThumbnail(material)
    img.loading = idx < 8 ? 'eager' : 'lazy'
    img.alt = material.title || 'Art'
    img.onerror = function () {
      const fallbacks = [material.main_image_url, material.url]
      const next = fallbacks.find(u => u && u !== this.src)
      if (next) this.src = next
      else this.style.display = 'none'
    }

    item.appendChild(img)
    grid.appendChild(item)
  })

  body.innerHTML = ''
  body.appendChild(grid)
}

function renderProductsContent() {
  const body = document.getElementById('wsMobileSheetBody-products')
  if (!body) return

  const products = window.ProductManager?.state?.products || []

  if (products.length === 0) {
    body.innerHTML = `
      <div class="ws-mobile-sheet-grid">
        ${Array(4).fill('<div class="ws-mobile-skeleton ws-mobile-skeleton-card"></div>').join('')}
      </div>
    `
    return
  }

  const grid = document.createElement('div')
  grid.className = 'ws-mobile-sheet-grid'

  const currentId = store.getState('design.currentProduct')?.sds_id

  products.forEach((product, idx) => {
    const item = document.createElement('div')
    const isActive = String(currentId) === String(product.sds_id)
    item.className = 'ws-mobile-product-item' + (isActive ? ' active' : '')
    item.onclick = () => {
      window.ProductManager?.selectProduct?.(product)
      closeSheet()
    }

    const img = document.createElement('img')
    img.src = getCdnThumbnail(product)
    img.loading = idx < 8 ? 'eager' : 'lazy'
    img.alt = product.sds_par_english_name || 'Product' // legacy-ref-ok: v3.2 rename pending
    img.onerror = function () {
      const fallbacks = [product.thumbnail_url, product.main_image_url, product.url]
      const next = fallbacks.find(u => u && u !== this.src)
      if (next) this.src = next
      else this.style.display = 'none'
    }

    item.appendChild(img)
    grid.appendChild(item)
  })

  body.innerHTML = ''
  body.appendChild(grid)
}

function setupSheetDrag(handle, sheet) {
  let startY = 0
  let startTranslate = 0
  let isDragging = false

  handle.addEventListener('touchstart', (e) => {
    startY = e.touches[0].clientY
    const transform = getComputedStyle(sheet).transform
    const matrix = new DOMMatrix(transform)
    startTranslate = matrix.m42
    isDragging = true
    sheet.style.transition = 'none'
  }, { passive: true })

  handle.addEventListener('touchmove', (e) => {
    if (!isDragging) return
    const deltaY = e.touches[0].clientY - startY
    const newTranslate = Math.max(0, startTranslate + deltaY)
    sheet.style.transform = `translateY(${newTranslate}px)`
  }, { passive: true })

  handle.addEventListener('touchend', () => {
    if (!isDragging) return
    isDragging = false
    sheet.style.transition = ''

    const sheetHeight = sheet.offsetHeight
    const transform = getComputedStyle(sheet).transform
    const matrix = new DOMMatrix(transform)
    const currentY = matrix.m42
    const ratio = currentY / sheetHeight

    if (ratio > 0.6) {
      closeSheet()
    } else if (ratio > 0.3) {
      sheet.classList.add('half')
      sheet.classList.remove('open', 'peek')
      sheet.style.transform = ''
    } else {
      sheet.classList.add('open')
      sheet.classList.remove('half', 'peek')
      sheet.style.transform = ''
    }
  }, { passive: true })
}
